import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Wallet, TrendingUp, PieChart, HelpCircle, ArrowLeft, KeyRound } from "lucide-react";
import DebugInfo from "@/components/DebugInfo";

export default function Login() {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [passwordHint, setPasswordHint] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [hintLoading, setHintLoading] = useState(false);
  const [showHint, setShowHint] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [resetStep, setResetStep] = useState(1); // 1 = enter username, 2 = enter new password
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setDisplayName("");
    setPasswordHint("");
    setInviteCode("");
    setShowHint(null);
    setLastError(null);
    setResetStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[LOGIN] Form submitted', { mode, username, timestamp: new Date().toISOString() });
    setLoading(true);
    setLastError(null);

    try {
      if (mode === 'login') {
        console.log('[LOGIN] Calling login function...');
        await login(username, password);
        console.log('[LOGIN] Login succeeded, navigating to home');
        toast.success("Welcome back!");
        navigate("/");
      } else if (mode === 'register') {
        console.log('[LOGIN] Calling register function...');
        await register(username, password, displayName, passwordHint || null, inviteCode || null);
        console.log('[LOGIN] Registration succeeded');
        toast.success("Account created successfully!");
        navigate("/");
      }
    } catch (error) {
      const errorInfo = {
        status: error.response?.status,
        detail: error.response?.data?.detail,
        message: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      };
      console.error('[LOGIN] Error occurred', errorInfo);
      setLastError(errorInfo);
      toast.error(error.response?.data?.detail || error.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleShowHint = async () => {
    if (!username.trim()) {
      toast.error("Please enter your username first");
      return;
    }
    
    setHintLoading(true);
    try {
      const response = await api.get(`/auth/hint/${encodeURIComponent(username)}`);
      if (response.data.hint) {
        setShowHint(response.data.hint);
      } else {
        toast.info("No password hint set for this account");
        setShowHint(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Username not found");
      } else {
        toast.error("Failed to retrieve hint");
      }
      setShowHint(null);
    } finally {
      setHintLoading(false);
    }
  };

  const handleForgotVerify = async () => {
    if (!username.trim()) {
      toast.error("Please enter your username");
      return;
    }

    setLoading(true);
    try {
      // Check if user exists by trying to get hint
      const response = await api.get(`/auth/hint/${encodeURIComponent(username)}`);
      if (response.data.hint) {
        setShowHint(response.data.hint);
      }
      setResetStep(2);
      toast.success("Username verified! Now set your new password.");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Username not found");
      } else {
        toast.error("Failed to verify username");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        username: username,
        new_password: newPassword
      });
      toast.success("Password reset successfully! Please login.");
      resetForm();
      setMode('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    resetForm();
    setMode(newMode);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col justify-center items-center login-bg p-12">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="p-3 bg-sky-400 rounded-xl">
              <Wallet className="h-8 w-8 text-slate-900" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight">
              MEF Companion
            </h1>
          </div>
          
          <p className="text-slate-300 text-lg mb-12 leading-relaxed">
            Your YNAB companion for budget goals and spending insights.
          </p>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left">
              <TrendingUp className="h-6 w-6 text-emerald-400 mb-3" strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-1">Track Progress</h3>
              <p className="text-slate-400 text-sm">See how you're pacing vs your annual goals</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-left">
              <PieChart className="h-6 w-6 text-sky-400 mb-3" strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-1">Category Budgets</h3>
              <p className="text-slate-400 text-sm">Set goals by category, see what's left</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div className="p-2 bg-slate-900 rounded-lg">
                <Wallet className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-bold font-['Outfit'] tracking-tight">MEF Companion</span>
            </div>
            <CardTitle className="text-2xl font-['Outfit'] tracking-tight">
              {mode === 'login' && "Welcome back"}
              {mode === 'register' && "Create account"}
              {mode === 'forgot' && "Reset password"}
            </CardTitle>
            <CardDescription>
              {mode === 'login' && "Enter your credentials to access your budget"}
              {mode === 'register' && "Set up your account to start tracking"}
              {mode === 'forgot' && (resetStep === 1 ? "Enter your username to reset password" : "Set your new password")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* FORGOT PASSWORD MODE */}
            {mode === 'forgot' && (
              <div className="space-y-4">
                {resetStep === 1 ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="reset-username">Username</Label>
                      <Input
                        id="reset-username"
                        placeholder="Enter your username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                      />
                    </div>

                    <Button 
                      onClick={handleForgotVerify}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify Username"}
                    </Button>
                  </>
                ) : (
                  <form onSubmit={handlePasswordReset} className="space-y-4">
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                      <p className="text-sm text-emerald-800">
                        <span className="font-medium">Username verified:</span> {username}
                      </p>
                      {showHint && (
                        <p className="text-sm text-emerald-700 mt-1">
                          <span className="font-medium">Your hint:</span> {showHint}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>

                    <Button 
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                          Resetting...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <KeyRound className="h-4 w-4" />
                          Reset Password
                        </span>
                      )}
                    </Button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="w-full text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </button>
              </div>
            )}

            {/* LOGIN / REGISTER MODE */}
            {(mode === 'login' || mode === 'register') && (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <div className="space-y-2">
                      <Label htmlFor="displayName">Display Name</Label>
                      <Input
                        id="displayName"
                        data-testid="display-name-input"
                        placeholder="Your name"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        required={mode === 'register'}
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      data-testid="username-input"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    {mode === 'login' && (
                      <div className="pt-1 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleShowHint}
                          disabled={hintLoading}
                          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                        >
                          <HelpCircle className="h-3 w-3" />
                          {hintLoading ? "Loading..." : "Show hint"}
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                          type="button"
                          onClick={() => switchMode('forgot')}
                          className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1 transition-colors"
                        >
                          <KeyRound className="h-3 w-3" />
                          Forgot password?
                        </button>
                      </div>
                    )}
                    {showHint && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                        <span className="font-medium">Hint:</span> {showHint}
                      </div>
                    )}
                  </div>

                  {mode === 'register' && (
                    <div className="space-y-2">
                      <Label htmlFor="passwordHint">Password Hint (optional)</Label>
                      <Input
                        id="passwordHint"
                        data-testid="password-hint-input"
                        placeholder="A hint to remember your password"
                        value={passwordHint}
                        onChange={(e) => setPasswordHint(e.target.value)}
                      />
                      <p className="text-xs text-slate-500">This will be shown if you forget your password</p>
                    </div>
                  )}

                  {mode === 'register' && (
                    <div className="space-y-2">
                      <Label htmlFor="inviteCode">Invite Code <span className="text-red-500">*</span></Label>
                      <Input
                        id="inviteCode"
                        data-testid="invite-code-input"
                        placeholder="Enter invite code"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        required
                        className="font-mono"
                      />
                      <p className="text-xs text-slate-500">Ask the account owner for the invite code</p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    disabled={loading}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                        {mode === 'login' ? "Signing in..." : "Creating account..."}
                      </span>
                    ) : (
                      mode === 'login' ? "Sign in" : "Create account"
                    )}
                  </Button>

                  {/* Error details for debugging */}
                  {lastError && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs">
                      <p className="font-medium text-red-800 mb-1">Login Error Details:</p>
                      <div className="text-red-700 font-mono space-y-0.5">
                        {lastError.status && <p>Status: {lastError.status}</p>}
                        {lastError.detail && <p>Detail: {lastError.detail}</p>}
                        {lastError.message && <p>Message: {lastError.message}</p>}
                        {lastError.code && <p>Code: {lastError.code}</p>}
                        <p className="text-red-500 text-[10px]">{lastError.timestamp}</p>
                      </div>
                    </div>
                  )}
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                    className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="toggle-auth-mode"
                  >
                    {mode === 'login' 
                      ? "Don't have an account? Create one" 
                      : "Already have an account? Sign in"}
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Debug Info Bar - visible on login page too */}
      <DebugInfo />
    </div>
  );
}
